/**
 * Financial Priority Waterfall Utility
 * Provides personalized financial priority recommendations based on complete financial picture
 * Follows industry best practices with UK and US specific guidance
 */

import { categorizeDebtByInterest } from './debtOptimization';
import { categorizeGoalsByTimeHorizon } from './goalBasedAllocation';

/**
 * Calculate comprehensive financial priority waterfall
 * @param {Object} financialData - Complete financial picture
 * @returns {Object} Prioritized action plan
 */
export const calculateFinancialPriorityWaterfall = (financialData) => {
  const {
    monthlyIncome = 0,
    monthlyExpenses = 0,
    cashReserves = 0,
    liabilities = [],
    financialGoals = [],
    hasEmployerMatch = false,
    employerMatchPercentage = 0,
    currentRetirementContribution = 0,
    retirementAccountType = null, // '401k', 'IRA', 'SIPP', 'ISA', etc.
    country = 'US'
  } = financialData;

  const priorities = [];
  let currentStep = 1;

  // Calculate key metrics
  const emergencyFundTarget = monthlyExpenses * 6;
  const emergencyFundMinimum = monthlyExpenses * 3;
  const emergencyFundProgress = cashReserves / emergencyFundTarget;

  const categorizedDebts = categorizeDebtByInterest(liabilities);
  const categorizedGoals = categorizeGoalsByTimeHorizon(financialGoals);

  const totalMonthlyDebtPayments = liabilities.reduce((sum, debt) =>
    sum + (parseFloat(debt.monthlyPayment) || 0), 0
  );

  const discretionaryIncome = monthlyIncome - monthlyExpenses - totalMonthlyDebtPayments;
  const debtToIncomeRatio = monthlyIncome > 0 ? (totalMonthlyDebtPayments / monthlyIncome) * 100 : 0;

  // PRIORITY 1: Emergency Fund Foundation (3 months minimum)
  if (cashReserves < emergencyFundMinimum) {
    priorities.push({
      step: currentStep++,
      priority: 'critical',
      category: 'Emergency Fund',
      title: 'Build 3-Month Emergency Fund',
      description: `Establish financial safety net before anything else`,
      target: emergencyFundMinimum,
      current: cashReserves,
      gap: emergencyFundMinimum - cashReserves,
      progress: (cashReserves / emergencyFundMinimum) * 100,
      rationale: country === 'UK'
        ? 'In the UK, average redundancy takes 3-4 months to resolve. Having 3 months expenses in instant-access savings or Premium Bonds prevents forced asset sales.'
        : 'US unemployment benefits average 26 weeks. A 3-month cushion prevents selling investments during downturns or accumulating high-interest debt.',
      action: `Save ${formatMonthlyAmount(emergencyFundMinimum / 6, country)} per month for 6 months`,
      status: 'in_progress'
    });
  }

  // PRIORITY 2: Employer Retirement Match (Free Money!)
  if (hasEmployerMatch && currentRetirementContribution < employerMatchPercentage) {
    const matchGap = (employerMatchPercentage - currentRetirementContribution) * monthlyIncome / 100;
    priorities.push({
      step: currentStep++,
      priority: 'critical',
      category: 'Retirement',
      title: country === 'UK' ? 'Max Employer Pension Match' : 'Max Employer 401(k) Match',
      description: `Instant 100% return on your money`,
      target: employerMatchPercentage,
      current: currentRetirementContribution,
      gap: matchGap,
      progress: (currentRetirementContribution / employerMatchPercentage) * 100,
      rationale: country === 'UK'
        ? `UK employers typically match 3-6% into workplace pensions. Not taking this is leaving free money on the table. Plus, contributions get tax relief at your marginal rate (20%, 40%, or 45%).`
        : `Employer 401(k) match is free money with immediate 100% return. If your employer matches 6% and you only contribute 3%, you\'re giving up 3% of your salary annually.`,
      action: `Increase retirement contribution to ${employerMatchPercentage}%`,
      status: 'pending'
    });
  }

  // PRIORITY 3: High-Interest Debt (>15% APR)
  if (categorizedDebts.critical.length > 0 || categorizedDebts.high.length > 0) {
    const highInterestDebts = [...categorizedDebts.critical, ...categorizedDebts.high];
    const totalHighInterestDebt = highInterestDebts.reduce((sum, d) =>
      sum + (parseFloat(d.currentBalance) || 0), 0
    );
    const avgHighInterestRate = highInterestDebts.reduce((sum, d) =>
      sum + (parseFloat(d.interestRate) || 0), 0
    ) / highInterestDebts.length;

    priorities.push({
      step: currentStep++,
      priority: 'critical',
      category: 'Debt Payoff',
      title: 'Eliminate High-Interest Debt',
      description: `${highInterestDebts.length} debt(s) at ${avgHighInterestRate.toFixed(1)}% average rate`,
      target: 0,
      current: totalHighInterestDebt,
      gap: totalHighInterestDebt,
      progress: 0,
      rationale: country === 'UK'
        ? `UK credit cards average 20-24% APR. Paying £1,000 at 22% costs £220/year in interest. This exceeds any investment return and compounds against you. Consider 0% balance transfer cards (typical 3% fee for 18-24 months).`
        : `Credit card debt at 18-24% APR is a financial emergency. The S&P 500 averages 10% annually, so carrying 20% debt while investing creates a negative 10% spread. Attack this first using avalanche method.`,
      action: `Use avalanche method: attack ${highInterestDebts[0]?.type} first`,
      status: 'pending',
      urgency: 'high'
    });
  }

  // PRIORITY 4: Complete Emergency Fund (6 months)
  if (cashReserves >= emergencyFundMinimum && cashReserves < emergencyFundTarget) {
    priorities.push({
      step: currentStep++,
      priority: 'high',
      category: 'Emergency Fund',
      title: 'Complete 6-Month Emergency Fund',
      description: `Increase from ${(emergencyFundProgress * 100).toFixed(0)}% to 100%`,
      target: emergencyFundTarget,
      current: cashReserves,
      gap: emergencyFundTarget - cashReserves,
      progress: emergencyFundProgress * 100,
      rationale: country === 'UK'
        ? `With 3 months covered, now build to 6 months for complete peace of mind. Keep in instant-access savings (4-5% APY) or split between Cash ISA and Premium Bonds for tax-free returns.`
        : `Upgrade from survival mode (3 months) to security mode (6 months). High-yield savings accounts currently offer 4-5% APY. This provides complete protection against job loss, medical emergencies, or major repairs.`,
      action: `Add ${formatMonthlyAmount((emergencyFundTarget - cashReserves) / 12, country)} monthly`,
      status: 'pending'
    });
  }

  // PRIORITY 5: Tax-Advantaged Retirement Accounts
  const retirementContributionMax = country === 'UK' ? 60000 : 23000; // UK: £60k annual allowance, US: $23k 401k limit
  const currentAnnualContribution = currentRetirementContribution * monthlyIncome * 12 / 100;

  if (currentAnnualContribution < retirementContributionMax * 0.15) { // If contributing less than 15% of max
    priorities.push({
      step: currentStep++,
      priority: 'high',
      category: 'Retirement',
      title: country === 'UK' ? 'Maximize ISA & SIPP Contributions' : 'Maximize 401(k) & IRA Contributions',
      description: `Tax-advantaged growth for long-term wealth`,
      target: retirementContributionMax,
      current: currentAnnualContribution,
      gap: retirementContributionMax - currentAnnualContribution,
      progress: (currentAnnualContribution / retirementContributionMax) * 100,
      rationale: country === 'UK'
        ? `UK offers £20k annual ISA allowance (tax-free growth forever) and £60k pension allowance (with tax relief). A 40% taxpayer contributing £10k gets £14k in their pension. Use ISAs for flexibility, SIPPs for retirement.`
        : `401(k) limit: $23k ($30.5k if 50+), IRA limit: $7k ($8k if 50+). Tax-deferred growth means a $10k investment becomes $74k in 30 years at 7%, vs $54k in taxable accounts after taxes.`,
      action: country === 'UK'
        ? `Max £20k ISA allowance, then SIPP contributions`
        : `Increase 401(k) to 15-20%, then max Roth IRA`,
      status: 'pending'
    });
  }

  // PRIORITY 6: Medium-Interest Debt (7-15%)
  if (categorizedDebts.medium.length > 0) {
    const totalMediumDebt = categorizedDebts.medium.reduce((sum, d) =>
      sum + (parseFloat(d.currentBalance) || 0), 0
    );
    const avgMediumRate = categorizedDebts.medium.reduce((sum, d) =>
      sum + (parseFloat(d.interestRate) || 0), 0
    ) / categorizedDebts.medium.length;

    priorities.push({
      step: currentStep++,
      priority: 'medium',
      category: 'Debt Payoff',
      title: 'Address Medium-Interest Debt',
      description: `${categorizedDebts.medium.length} debt(s) at ${avgMediumRate.toFixed(1)}% average`,
      target: 0,
      current: totalMediumDebt,
      gap: totalMediumDebt,
      progress: 0,
      rationale: `At ${avgMediumRate.toFixed(1)}%, this is in the "gray zone" - close to historical stock returns. Balance minimum payments with retirement investing. Consider refinancing if rates have dropped.`,
      action: `Make minimum payments while maxing retirement accounts`,
      status: 'pending'
    });
  }

  // PRIORITY 7: Short & Medium-Term Goals
  const urgentGoals = [...categorizedGoals.short, ...categorizedGoals.medium];
  if (urgentGoals.length > 0) {
    urgentGoals.forEach(goal => {
      const target = parseFloat(goal.targetAmount) || 0;
      const current = parseFloat(goal.currentAmount) || 0;
      const monthsRemaining = Math.ceil((new Date(goal.targetDate) - new Date()) / (1000 * 60 * 60 * 24 * 30));

      priorities.push({
        step: currentStep++,
        priority: monthsRemaining <= 12 ? 'high' : 'medium',
        category: 'Goals',
        title: `Fund ${goal.type} Goal`,
        description: `${monthsRemaining} months until target date`,
        target: target,
        current: current,
        gap: target - current,
        progress: (current / target) * 100,
        rationale: `Goal-based allocation: ${monthsRemaining <= 36 ? '0% stocks, 80% cash' : '50% stocks, 40% bonds'}. Timeline is too short for full equity exposure.`,
        action: `Save ${formatMonthlyAmount((target - current) / monthsRemaining, country)} monthly`,
        status: current / target >= 0.75 ? 'on_track' : 'behind'
      });
    });
  }

  // PRIORITY 8: Low-Interest Debt (<7%)
  if (categorizedDebts.low.length > 0) {
    const totalLowDebt = categorizedDebts.low.reduce((sum, d) =>
      sum + (parseFloat(d.currentBalance) || 0), 0
    );
    const avgLowRate = categorizedDebts.low.reduce((sum, d) =>
      sum + (parseFloat(d.interestRate) || 0), 0
    ) / categorizedDebts.low.length;

    priorities.push({
      step: currentStep++,
      priority: 'low',
      category: 'Debt Payoff',
      title: 'Consider Low-Interest Debt',
      description: `${categorizedDebts.low.length} debt(s) at ${avgLowRate.toFixed(1)}% - optional`,
      target: 0,
      current: totalLowDebt,
      gap: totalLowDebt,
      progress: 0,
      rationale: country === 'UK'
        ? `At ${avgLowRate.toFixed(1)}%, this is "good debt" (below inflation + investment returns). UK mortgages at 3-4% should not be accelerated - invest the difference instead. Student loans on Plan 2 get written off after 30 years.`
        : `${avgLowRate.toFixed(1)}% is below historical S&P 500 returns (10%). Mortgages at 3-4% with tax deductions are essentially free money. Federal student loans may qualify for forgiveness programs. Make minimums and invest the rest.`,
      action: `Minimum payments only - invest the difference`,
      status: 'optional'
    });
  }

  // PRIORITY 9: Long-Term Goals & Additional Investments
  if (categorizedGoals.long.length > 0 || discretionaryIncome > 0) {
    priorities.push({
      step: currentStep++,
      priority: 'low',
      category: 'Wealth Building',
      title: 'Long-Term Goals & Taxable Investing',
      description: `Build wealth beyond retirement accounts`,
      rationale: country === 'UK'
        ? `After maxing ISA (£20k) and pension allowances, use taxable General Investment Accounts (GIA). Focus on tax-efficient index funds. Capital gains allowance: £3,000 (2024/25).`
        : `After maxing 401(k) and IRA, invest in taxable brokerage accounts. Use tax-efficient index funds (avoid high-dividend funds). Long-term capital gains taxed at 0%, 15%, or 20% depending on income.`,
      action: `Invest surplus in low-cost index funds`,
      status: 'future'
    });
  }

  return {
    priorities,
    summary: {
      total_steps: priorities.length,
      critical_priorities: priorities.filter(p => p.priority === 'critical').length,
      high_priorities: priorities.filter(p => p.priority === 'high').length,
      discretionary_income: discretionaryIncome,
      debt_to_income_ratio: debtToIncomeRatio,
      emergency_fund_status: getEmergencyFundStatus(cashReserves, emergencyFundTarget),
      recommended_focus: priorities[0]?.title || 'Continue current strategy'
    },
    country_specific_notes: getCountrySpecificNotes(country)
  };
};

/**
 * Get emergency fund status
 */
function getEmergencyFundStatus(current, target) {
  const ratio = current / target;
  if (ratio >= 1) return 'complete';
  if (ratio >= 0.5) return 'halfway';
  if (ratio >= 0.25) return 'started';
  return 'insufficient';
}

/**
 * Format monthly amount with currency
 */
function formatMonthlyAmount(amount, country) {
  const currency = country === 'UK' ? '£' : '$';
  return `${currency}${Math.round(amount).toLocaleString()}`;
}

/**
 * Get country-specific financial guidance notes
 */
function getCountrySpecificNotes(country) {
  if (country === 'UK') {
    return {
      tax_advantaged_accounts: [
        'ISA: £20,000 annual allowance, tax-free growth forever',
        'SIPP: £60,000 annual allowance with tax relief (20-45%)',
        'Lifetime ISA: £4,000 allowance with 25% government bonus (age <40, first home or retirement)'
      ],
      debt_considerations: [
        'Student Loans Plan 2: Repayment threshold £27,295, written off after 30 years',
        'Mortgage overpayments: Check early repayment charges (typically 1-5% of balance)',
        '0% balance transfer: Typical 3% fee for 18-24 months interest-free'
      ],
      emergency_fund_options: [
        'Premium Bonds: Chance of prizes, 4.4% average return, instant access',
        'Cash ISA: 4-5% fixed rates, tax-free interest',
        'Instant-access savings: 4-5% APY from online banks'
      ]
    };
  } else {
    return {
      tax_advantaged_accounts: [
        '401(k): $23,000 limit ($30,500 if age 50+)',
        'IRA: $7,000 limit ($8,000 if age 50+)',
        'Roth IRA: Tax-free growth, income limits apply',
        'HSA: $4,150 individual/$8,300 family, triple tax advantage'
      ],
      debt_considerations: [
        'Federal Student Loans: Income-driven repayment, PSLF after 10 years',
        'Mortgage interest deduction: Itemize if mortgage interest > standard deduction',
        '401(k) loans: Borrow up to $50k or 50% vested balance, risky but low-interest'
      ],
      emergency_fund_options: [
        'High-yield savings: 4-5% APY from online banks',
        'I-Bonds: Inflation-protected, 1-year lockup, 3-month penalty if <5 years',
        'Money market funds: Competitive rates, check-writing access'
      ]
    };
  }
}

/**
 * Calculate optimal monthly allocation across priorities
 */
export const calculateOptimalAllocation = (priorities, monthlyDiscretionary) => {
  if (monthlyDiscretionary <= 0 || priorities.length === 0) {
    return [];
  }

  const allocations = [];
  let remainingBudget = monthlyDiscretionary;

  // Allocate to critical priorities first
  const critical = priorities.filter(p => p.priority === 'critical' && p.status !== 'complete');
  const high = priorities.filter(p => p.priority === 'high' && p.status !== 'complete');

  // Split budget across critical priorities
  if (critical.length > 0) {
    const perCritical = remainingBudget / critical.length;
    critical.forEach(p => {
      allocations.push({
        priority: p.title,
        amount: Math.min(perCritical, p.gap || 0),
        rationale: 'Critical priority - address immediately'
      });
    });
  } else if (high.length > 0) {
    // If no critical, fund high priorities
    const perHigh = remainingBudget / high.length;
    high.forEach(p => {
      allocations.push({
        priority: p.title,
        amount: Math.min(perHigh, p.gap || 0),
        rationale: 'High priority - focus here'
      });
    });
  }

  return allocations;
};
